import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Skeleton,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import { sendMessageToAgent } from "../../../store/userSlice";
import { v4 as uuid } from "uuid";
import axiosClient from "../../../api/AxiosClient";
import styles from "./styles.module.css";

interface LineItem {
  grade: number;
  effort: number;
  is_uae: boolean;
  quantity: number;
  job_title: string;
  job_family: string;
  sub_family: string;
  level_title: string;
  months_duration: number;
  amount_usd: number;
  ctc_midpoint_aed: number;
}

interface EstimationRecord {
  id: number;
  project_name: string;
  serial_number: string;
  email: string;
  version: string;
  quantity: number;
  final_value: string;
  group_id: string;
  last_updated_date: string;
  line_items: LineItem[];
}

interface PaginationInfo {
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

interface ApiResponse {
  data: EstimationRecord[];
  pagination: PaginationInfo;
}

interface ManpowerCostExistingInfoProps {
  parsedContent?: any;
  messageId?: string;
}

export default function ManpowerCostExistingInfo({
  parsedContent,
  messageId,
}: ManpowerCostExistingInfoProps): JSX.Element {
  const dispatch = useAppDispatch();
  const currentAgent = useSelector(
    (state: RootState) => state.agents?.currentAgent,
  );
  const agentLoading = useSelector((state: RootState) => state.user.loading);
  const currentChat = useSelector((state: RootState) => state.user.currentChat);

  // State management
  const [records, setRecords] = useState<EstimationRecord[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    total: 0,
    limit: 10,
    offset: 0,
    hasMore: false,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRecordId, setSelectedRecordId] = useState<number | null>(null);

  // Filter states
  const [projectNameFilter, setProjectNameFilter] = useState("");
  const [serialNumberFilter, setSerialNumberFilter] = useState("");

  // Debounced filter values
  const [debouncedProjectName, setDebouncedProjectName] = useState("");
  const [debouncedSerialNumber, setDebouncedSerialNumber] = useState("");

  // Session ID
  const sessionId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("sessionId");
  }, []);

  // Check if this is the latest message
  const isLatestMessage = useMemo(() => {
    if (!messageId) return true;
    const lastAgentMessage = [...currentChat]
      .reverse()
      .find((msg) => msg.sender === "agent");
    return lastAgentMessage?.id === messageId;
  }, [currentChat, messageId]);

  // Debounce filter inputs (500ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedProjectName(projectNameFilter);
    }, 500);
    return () => clearTimeout(timer);
  }, [projectNameFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSerialNumber(serialNumberFilter);
    }, 500);
    return () => clearTimeout(timer);
  }, [serialNumberFilter]);

  // Fetch records from API
  const fetchRecords = useCallback(
    async (offset: number = 0) => {
      try {
        setLoading(true);
        setError(null);

        const params = new URLSearchParams({
          limit: pagination.limit.toString(),
          offset: offset.toString(),
        });

        // Add filters if present
        if (debouncedProjectName.trim()) {
          params.append("project_name", debouncedProjectName.trim());
        }
        if (debouncedSerialNumber.trim()) {
          params.append("serial_number", debouncedSerialNumber.trim());
        }

        const response = await axiosClient.get<ApiResponse>(
          `/commercial-calculator-records?${params.toString()}`,
        );

        setRecords(response.data.data || []);
        setPagination(response.data.pagination);
      } catch (err) {
        console.error("Error fetching records:", err);
        setError("Failed to load estimation records. Please try again.");
        setRecords([]);
      } finally {
        setLoading(false);
      }
    },
    [pagination.limit, debouncedProjectName, debouncedSerialNumber],
  );

  // Initial fetch and refetch on filter change
  useEffect(() => {
    fetchRecords(0);
  }, [debouncedProjectName, debouncedSerialNumber]);

  // Handle pagination
  const handlePreviousPage = () => {
    const newOffset = Math.max(0, pagination.offset - pagination.limit);
    fetchRecords(newOffset);
  };

  const handleNextPage = () => {
    if (pagination.hasMore) {
      const newOffset = pagination.offset + pagination.limit;
      fetchRecords(newOffset);
    }
  };

  // Calculate current page info
  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;
  const totalPages = Math.ceil(pagination.total / pagination.limit);

  // Format final value with proper currency formatting (supports ranges)
  const formatFinalValue = useCallback((finalValue: string): string => {
    if (!finalValue) return "0";

    const valueStr = finalValue.toString();

    // Check if it's a range (contains " - ")
    if (valueStr.includes(" - ")) {
      const [minValue, maxValue] = valueStr.split(" - ");
      const formattedMin = parseFloat(minValue).toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
      const formattedMax = parseFloat(maxValue).toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
      return `${formattedMin} - ${formattedMax}`;
    }

    // Single value (supports decimals)
    const value = parseFloat(valueStr);
    return value.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  }, []);

  // Handle Open button click
  const handleOpenRecord = (record: EstimationRecord) => {
    if (selectedRecordId || agentLoading || !currentAgent) return;

    setSelectedRecordId(record.id);

    const payload = {
      project_name: record.project_name,
      serial_number: record.serial_number,
      group_id: record.group_id,
      id: record.id,
      action: "calculate_manpower_cost",
      line_items: record.line_items,
    };

    dispatch(
      sendMessageToAgent({
        agentId: currentAgent.id,
        message: JSON.stringify(payload),
        questionId: uuid(),
        timestamp: new Date().toISOString(),
        agentEndpoint: currentAgent.endpoint,
        sessionId,
      }),
    );
  };

  // Loading skeleton
  const renderLoadingSkeleton = () => (
    <TableContainer component={Paper} className={styles.tableContainer}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>PROJECT NAME</TableCell>
            <TableCell>SERIAL NUMBER</TableCell>
            <TableCell>EMAIL</TableCell>
            <TableCell>VERSION</TableCell>
            <TableCell>RESOURCES</TableCell>
            <TableCell>FINAL VALUE (USD)</TableCell>
            <TableCell>LAST UPDATED</TableCell>
            <TableCell align="right"></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {[...Array(5)].map((_, index) => (
            <TableRow key={index}>
              <TableCell>
                <Skeleton variant="text" width={60} />
              </TableCell>
              <TableCell>
                <Skeleton variant="text" width={200} />
              </TableCell>
              <TableCell>
                <Skeleton variant="text" width={100} />
              </TableCell>
              <TableCell>
                <Skeleton variant="text" width={180} />
              </TableCell>
              <TableCell>
                <Skeleton variant="text" width={40} />
              </TableCell>
              <TableCell>
                <Skeleton variant="text" width={40} />
              </TableCell>
              <TableCell>
                <Skeleton variant="text" width={120} />
              </TableCell>
              <TableCell>
                <Skeleton variant="text" width={100} />
              </TableCell>
              <TableCell>
                <Skeleton variant="rectangular" width={70} height={36} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box className={styles.container}>
      {/* Header */}
      <Box className={styles.header}>
        <Box className={styles.headerIcon}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="#00A3A3">
            <circle cx="12" cy="12" r="3" />
          </svg>
        </Box>
        <Typography variant="subtitle2" className={styles.headerText}>
          MANPOWER COST CALCULATOR
        </Typography>
      </Box>

      {/* Instruction */}
      <Typography className={styles.instruction}>
        Search or select to filter, then choose a record to open and edit.
      </Typography>

      {/* Filter Inputs */}
      <Box className={styles.filterContainer}>
        <Box className={styles.filterField}>
          <Typography className={styles.filterLabel}>PROJECT NAME</Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Type or select"
            value={projectNameFilter}
            onChange={(e) => setProjectNameFilter(e.target.value)}
            disabled={!isLatestMessage}
            className={styles.filterInput}
          />
        </Box>
        <Box className={styles.filterField}>
          <Typography className={styles.filterLabel}>SERIAL NUMBER</Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Type or select"
            value={serialNumberFilter}
            onChange={(e) => setSerialNumberFilter(e.target.value)}
            disabled={!isLatestMessage}
            className={styles.filterInput}
          />
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Table */}
      {loading ? (
        renderLoadingSkeleton()
      ) : records.length === 0 ? (
        <Box className={styles.emptyState}>
          <Typography className={styles.emptyStateText}>
            No estimation records found.
            {(debouncedProjectName || debouncedSerialNumber) &&
              " Try adjusting your filters."}
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} className={styles.tableContainer}>
          <Table>
            <TableHead>
              <TableRow className={styles.tableHeaderRow}>
                <TableCell className={styles.tableHeaderCell}>ID</TableCell>
                <TableCell className={styles.tableHeaderCell}>
                  PROJECT NAME
                </TableCell>
                <TableCell className={styles.tableHeaderCell}>
                  SERIAL NUMBER
                </TableCell>
                <TableCell className={styles.tableHeaderCell}>EMAIL</TableCell>
                <TableCell className={styles.tableHeaderCell}>
                  VERSION
                </TableCell>
                <TableCell className={styles.tableHeaderCell}>
                  RESOURCES
                </TableCell>
                <TableCell className={styles.tableHeaderCell}>
                  FINAL VALUE (USD)
                </TableCell>
                <TableCell className={styles.tableHeaderCell}>
                  LAST UPDATED
                </TableCell>
                <TableCell className={styles.tableHeaderCell} align="right">
                  {/* Action column */}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map((record) => (
                <TableRow key={record.id} className={styles.tableRow}>
                  <TableCell className={styles.tableCell}>
                    {record.id}
                  </TableCell>
                  <TableCell className={styles.tableCell}>
                    {record.project_name}
                  </TableCell>
                  <TableCell className={styles.tableCell}>
                    {record.serial_number}
                  </TableCell>
                  <TableCell className={styles.tableCell}>
                    {record.email}
                  </TableCell>
                  <TableCell className={styles.tableCell}>
                    {record.version}
                  </TableCell>
                  <TableCell className={styles.tableCell}>
                    {record.quantity}
                  </TableCell>
                  <TableCell className={styles.tableCell}>
                    USD {formatFinalValue(record.final_value)}
                  </TableCell>
                  <TableCell className={styles.tableCell}>
                    {record.last_updated_date}
                  </TableCell>
                  <TableCell className={styles.tableCell} align="right">
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => handleOpenRecord(record)}
                      disabled={
                        !isLatestMessage ||
                        selectedRecordId !== null ||
                        agentLoading
                      }
                      className={styles.openButton}
                    >
                      Open
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pagination */}
      {!loading && records.length > 0 && (
        <Box className={styles.pagination}>
          <IconButton
            onClick={handlePreviousPage}
            disabled={pagination.offset === 0 || !isLatestMessage}
            size="small"
            className={styles.paginationButton}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography className={styles.paginationText}>
            Page {currentPage} of {totalPages || 1} ({pagination.total} record
            {pagination.total !== 1 ? "s" : ""})
          </Typography>
          <IconButton
            onClick={handleNextPage}
            disabled={!pagination.hasMore || !isLatestMessage}
            size="small"
            className={styles.paginationButton}
          >
            <ArrowForwardIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
    </Box>
  );
}
